import React, { useState, useEffect, useMemo } from "react";
import DashboardLayout from "../DashboardLayout";
import apiClient from "../../utils/apiClient";
import server from "../../utils/serverConfig";
import { SearchIcon, Plus, X, List, Calendar, Layout, Trash2, Inbox, Filter, MoreHorizontal, BarChart2, ChevronDown, ArrowRightLeft, Info } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";

const API = server.godseye.baseUrl.replace(/\/$/, "");

const ClearStocks = () => {
    const [selectedCountry, setSelectedCountry] = useState("india"); // 'india' | 'us'
    const [defaultStocks, setDefaultStocks] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(false);
    
    const [dailyList, setDailyList] = useState([]);
    const [weeklyList, setWeeklyList] = useState([]);
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, stock: null, listType: null });
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [listsLoading, setListsLoading] = useState(false);

    // Fetch daily/weekly lists whenever country changes
    useEffect(() => {
        const fetchLists = async () => {
            setListsLoading(true);
            try {
                const [dailyRes, weeklyRes] = await Promise.all([
                    apiClient.get(`${API}/clearstocks/daily?country=${selectedCountry}`),
                    apiClient.get(`${API}/clearstocks/weekly?country=${selectedCountry}`)
                ]);

                if (dailyRes.data?.data) {
                    const mappedDaily = dailyRes.data.data.map(item => ({
                        ...item,
                        symTicker: item.symTicker || item.symticker,
                        symbol: item.symbol || item.exSymbol || (item.symTicker || item.symticker)?.split(':')[1]?.split('-')[0]
                    }));
                    setDailyList(mappedDaily);
                }
                if (weeklyRes.data?.data) {
                    const mappedWeekly = weeklyRes.data.data.map(item => ({
                        ...item,
                        symTicker: item.symTicker || item.symticker,
                        symbol: item.symbol || item.exSymbol || (item.symTicker || item.symticker)?.split(':')[1]?.split('-')[0]
                    }));
                    setWeeklyList(mappedWeekly);
                }
            } catch (error) {
                console.error("Failed to load lists:", error);
                toast.error("Failed to load daily/weekly stocks");
            } finally {
                setListsLoading(false);
            }
        };
        fetchLists();
    }, [selectedCountry]);

    // Fetch default stocks list (underlying stocks for India, not shown for US)
    useEffect(() => {
        if (selectedCountry !== "india") {
            setDefaultStocks([]);
            return;
        }
        const fetchStocks = async () => {
            setLoading(true);
            try {
                const response = await apiClient.get(`${API}/get-underlying-stocks-list`);
                const stocks = response.data.data || [];
                if (stocks.length === 0) {
                    setDefaultStocks([]);
                } else {
                    setDefaultStocks(stocks.sort((a, b) => a.symbol.localeCompare(b.symbol)));
                }
            } catch (error) {
                console.error("Failed to fetch stocks:", error);
                setDefaultStocks([]);
                toast.error("Failed to load stock list");
            } finally {
                setLoading(false);
            }
        };
        fetchStocks();
    }, [selectedCountry]);

    // Search via API when query >= 3 chars; country-aware
    useEffect(() => {
        if (searchQuery.length < 3) {
            setSearchResults([]);
            return;
        }

        const delayDebounceFn = setTimeout(async () => {
            setLoading(true);
            try {
                const response = await apiClient.get(
                    `${API}/clearstocks/search?q=${searchQuery}&country=${selectedCountry}`
                );
                const results = response.data.data || [];

                let mappedResults;
                if (selectedCountry === "us") {
                    // Alpaca format: symbol is raw ticker, name is company name
                    mappedResults = results.map(item => ({
                        ...item,
                        symbol: item.symbol,
                        name: item.name || "",
                        symTicker: item.symTicker || item.symbol,
                        country: "us"
                    }));
                } else {
                    // India format: exSymbol, exSymName etc.
                    mappedResults = results.map(item => ({
                        ...item,
                        symbol: item.exSymbol || item.symbol || item.short_name,
                        name: item.short_name || item.exSymName,
                        symTicker: item.symTicker,
                        country: "india"
                    }));
                }
                setSearchResults(mappedResults);
            } catch (error) {
                console.error("Search failed:", error);
            } finally {
                setLoading(false);
            }
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery, selectedCountry]);

    // Reset search when switching country
    const handleCountrySwitch = (country) => {
        setSelectedCountry(country);
        setSearchQuery("");
        setSearchResults([]);
        setDailyList([]);
        setWeeklyList([]);
    };

    const filteredStocks = useMemo(() => {
        if (searchQuery.length >= 3) return searchResults;
        if (!searchQuery) return defaultStocks;
        return defaultStocks.filter(stock =>
            stock.symbol.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [defaultStocks, searchResults, searchQuery]);

    const formatLargeNumber = (num) => {
        if (num === null || num === undefined) return "-";
        const val = Number(num);
        if (val >= 1e9) return `${(val / 1e9).toFixed(2)}B`;
        if (val >= 1e6) return `${(val / 1e6).toFixed(2)}M`;
        if (val >= 1e3) return `${(val / 1e3).toFixed(1)}K`;
        return val.toLocaleString('en-US');
    };

    const formatDollarValue = (num) => {
        if (num === null || num === undefined) return "-";
        const val = Number(num);
        if (val >= 1e9) return `$${(val / 1e9).toFixed(2)}B`;
        if (val >= 1e6) return `$${(val / 1e6).toFixed(2)}M`;
        if (val >= 1e3) return `$${(val / 1e3).toFixed(1)}K`;
        return `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleString('en-IN', {
            month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const getStockDisplayLabel = (stock) => {
        if (selectedCountry === "us") {
            return stock.symbol || stock.symTicker || "";
        }
        return stock.symTicker || `NSE:${stock.symbol}`;
    };

    const getStockSubLabel = (stock) => {
        if (selectedCountry === "us") {
            return stock.name ? stock.name.slice(0, 30) : "";
        }
        return null;
    };

    const addToDaily = async (stock) => {
        const uniqueKey = stock.symTicker || stock.symbol;
        if (dailyList.some(s => s.symTicker === uniqueKey || s.symbol === uniqueKey)) {
            toast.error(`${stock.symbol} is already in Daily list`);
            return;
        }
        const payload = {
            ...stock,
            symTicker: stock.symTicker || stock.symbol,
            country: selectedCountry,
            exSymName: selectedCountry === "us" ? (stock.name || "") : (stock.exSymName || ""),
            exchangeName: selectedCountry === "us" ? (stock.exchange || "") : (stock.exchangeName || "")
        };
        try {
            await apiClient.post(`${API}/clearstocks/daily`, payload);
            setDailyList(prev => [...prev, { ...payload, added_at: new Date().toISOString() }]);
            toast.success(`Added ${stock.symbol} to Daily`);
        } catch (error) {
            console.error("Add to daily failed:", error);
            toast.error("Failed to add to Daily list");
        }
    };

    const addToWeekly = async (stock) => {
        const uniqueKey = stock.symTicker || stock.symbol;
        if (weeklyList.some(s => s.symTicker === uniqueKey || s.symbol === uniqueKey)) {
            toast.error(`${stock.symbol} is already in Weekly list`);
            return;
        }
        const payload = {
            ...stock,
            symTicker: stock.symTicker || stock.symbol,
            country: selectedCountry,
            exSymName: selectedCountry === "us" ? (stock.name || "") : (stock.exSymName || ""),
            exchangeName: selectedCountry === "us" ? (stock.exchange || "") : (stock.exchangeName || "")
        };
        try {
            await apiClient.post(`${API}/clearstocks/weekly`, payload);
            setWeeklyList(prev => [...prev, { ...payload, added_at: new Date().toISOString() }]);
            toast.success(`Added ${stock.symbol} to Weekly`);
        } catch (error) {
            console.error("Add to weekly failed:", error);
            toast.error("Failed to add to Weekly list");
        }
    };

    const executeDelete = async () => {
        const { stock, listType } = deleteModal;
        if (!stock) return;

        const uniqueKey = stock.symTicker || stock.symbol;
        setDeleteLoading(true);
        try {
            if (listType === 'daily') {
                if (stock.symTicker) {
                    await apiClient.delete(`${API}/clearstocks/daily/${encodeURIComponent(stock.symTicker)}`);
                }
                setDailyList(prev => prev.filter(s => s.symTicker !== uniqueKey && s.symbol !== uniqueKey));
                toast.success("Removed from Daily");
            } else if (listType === 'weekly') {
                if (stock.symTicker) {
                    await apiClient.delete(`${API}/clearstocks/weekly/${encodeURIComponent(stock.symTicker)}`);
                }
                setWeeklyList(prev => prev.filter(s => s.symTicker !== uniqueKey && s.symbol !== uniqueKey));
                toast.success("Removed from Weekly");
            }
        } catch (error) {
            console.error(`Remove from ${listType} failed:`, error);
            toast.error(`Failed to remove from ${listType} list`);
        } finally {
            setDeleteLoading(false);
            setDeleteModal({ isOpen: false, stock: null, listType: null });
        }
    };

    const isIndia = selectedCountry === "india";
    const countryLabel = isIndia ? "IN" : "US";

    return (
        <DashboardLayout title="Clear Stocks">
            <div className="flex h-screen w-full overflow-hidden bg-[#845b95]">
                {/* Left Panel: Inbox / Stocks List */}
                <div className="w-[280px] flex flex-col bg-[#14233a] border-r border-[#1a2b4c]">
                    <div className="p-4 pb-2">
                        {/* Header row */}
                        <div className="flex items-center justify-between mb-3 text-white">
                            <h2 className="text-base font-bold flex items-center gap-2">
                                <Inbox className="w-5 h-5" />
                                Stocks List
                            </h2>
                            <div className="flex items-center gap-3 text-zinc-400">
                                <Filter className="w-4 h-4 hover:text-white cursor-pointer" />
                                <MoreHorizontal className="w-4 h-4 hover:text-white cursor-pointer" />
                            </div>
                        </div>

                        {/* Country Toggle: IN | US */}
                        <div className="flex items-center gap-1.5 mb-3 p-1 bg-[#0d1a2e] rounded-lg border border-[#1a2b4c]">
                            <button
                                onClick={() => handleCountrySwitch("india")}
                                className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md text-xs font-bold transition-all ${
                                    isIndia
                                        ? "bg-[#1a3a5c] text-white shadow-sm"
                                        : "text-zinc-500 hover:text-zinc-300"
                                }`}
                            >
                                <span>IN</span>
                            </button>
                            <button
                                onClick={() => handleCountrySwitch("us")}
                                className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md text-xs font-bold transition-all ${
                                    !isIndia
                                        ? "bg-[#1a3a5c] text-white shadow-sm"
                                        : "text-zinc-500 hover:text-zinc-300"
                                }`}
                            >
                                <span>US</span>
                            </button>
                        </div>

                        <input
                            type="text"
                            placeholder={isIndia ? "Search NSE/BSE symbol..." : "Search US equity (e.g. AAPL)..."}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full bg-[#22272b] border border-transparent rounded-lg px-3 py-2 text-sm text-[#b6c2cf] focus:outline-none focus:border-blue-500 transition-all placeholder:text-zinc-500"
                        />
                    </div>

                    <div className="flex-1 overflow-y-auto custom-scroll p-3 pt-2 space-y-2">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-10 gap-3">
                                <div className="w-6 h-6 border-2 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin"></div>
                                <span className="text-[10px] text-zinc-500 font-bold uppercase tracking-widest">Loading...</span>
                            </div>
                        ) : filteredStocks.length === 0 && searchQuery.length < 3 && selectedCountry === "us" ? (
                            <div className="flex flex-col items-center justify-center py-10 gap-2 text-center">
                                <span className="text-2xl">🇺🇸</span>
                                <p className="text-xs text-zinc-400 font-medium">Type at least 3 chars</p>
                                <p className="text-[10px] text-zinc-500">to search US equities</p>
                            </div>
                        ) : (
                            filteredStocks.map((stock) => (
                                <div
                                    key={stock.symTicker || stock.symbol}
                                    className="group flex flex-col justify-center px-3 py-2 min-h-[36px] rounded-lg bg-[#22272b] border border-transparent hover:border-zinc-600 transition-all cursor-pointer relative"
                                >
                                    <span className="text-sm font-medium text-[#b6c2cf] pr-14 truncate">
                                        {getStockDisplayLabel(stock)}
                                    </span>
                                    {getStockSubLabel(stock) && (
                                        <span className="text-[10px] text-zinc-500 truncate pr-14">
                                            {getStockSubLabel(stock)}
                                        </span>
                                    )}
                                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-[#22272b] pl-2">
                                        <button
                                            onClick={() => addToDaily(stock)}
                                            className="w-6 h-6 flex items-center justify-center rounded bg-[#7a6416] text-[#ffe066] text-xs font-bold hover:brightness-110"
                                            title="Add to Daily"
                                        >
                                            D
                                        </button>
                                        <button
                                            onClick={() => addToWeekly(stock)}
                                            className="w-6 h-6 flex items-center justify-center rounded bg-[#246348] text-[#6ee7b7] text-xs font-bold hover:brightness-110"
                                            title="Add to Weekly"
                                        >
                                            W
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Main Content Areas */}
                <div className="flex-1 overflow-x-auto p-4 flex flex-col">
                    {/* Header */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5 border-b border-white/10 pb-4">
                        <div className="flex items-center gap-3 text-white font-bold text-lg">
                            <h1>Clear Stocks</h1>
                            {/* Country badge */}
                            <span className="flex items-center gap-1 px-2 py-0.5 bg-white/10 rounded-md text-sm font-medium">
                                {countryLabel}
                            </span>
                        </div>

                        {/* Country switch in header too */}
                        <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1 p-0.5 bg-white/10 rounded-lg">
                                <button
                                    onClick={() => handleCountrySwitch("india")}
                                    className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${
                                        isIndia
                                            ? "bg-white/20 text-white"
                                            : "text-white/50 hover:text-white"
                                    }`}
                                >
                                    IN
                                </button>
                                <button
                                    onClick={() => handleCountrySwitch("us")}
                                    className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${
                                        !isIndia
                                            ? "bg-white/20 text-white"
                                            : "text-white/50 hover:text-white"
                                    }`}
                                >
                                    US
                                </button>
                            </div>
                            <button className="flex items-center gap-2 px-2.5 py-1.5 bg-white/20 hover:bg-white/30 rounded-md text-sm text-white transition-colors font-medium self-start">
                                <BarChart2 className="w-4 h-4" />
                                <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                            </button>
                        </div>
                    </div>

                    <div className="flex gap-4 h-full min-w-max items-start">
                            {/* Daily Column */}
                            <div className="w-[272px] flex flex-col max-h-full bg-[#7a6416] rounded-xl shadow-sm">
                                <div className="p-3 flex items-center justify-between">
                                    <h3 className="text-sm font-bold text-[#ffe066] flex items-center gap-1.5">
                                        Daily
                                    </h3>
                                    <div className="flex items-center gap-2 text-[#ffe066] opacity-80">
                                        <ArrowRightLeft className="w-4 h-4 cursor-pointer hover:opacity-100" />
                                        <MoreHorizontal className="w-4 h-4 cursor-pointer hover:opacity-100" />
                                    </div>
                                </div>
                                <div className="flex-1 overflow-y-auto custom-scroll px-2 space-y-2 pb-2">
                                    {listsLoading ? (
                                        <div className="flex flex-col items-center justify-center py-10 gap-3">
                                            <div className="w-6 h-6 border-2 border-yellow-500/20 border-t-[#ffe066] rounded-full animate-spin"></div>
                                        </div>
                                    ) : (
                                        <AnimatePresence initial={false}>
                                            {dailyList.length === 0 ? (
                                                <motion.div
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    transition={{ duration: 0.15 }}
                                                    className="flex flex-col items-center justify-center p-4 py-8 mt-2 text-center bg-[#ffe066]/5 rounded-lg border border-dashed border-[#ffe066]/20 mx-1"
                                                >
                                                    <Inbox className="w-8 h-8 text-[#ffe066]/40 mb-2" />
                                                    <p className="text-xs font-medium text-[#ffe066]/60">No Daily {countryLabel} Stocks</p>
                                                    <p className="text-[10px] text-[#ffe066]/40 mt-1">Add from the inbox panel</p>
                                                </motion.div>
                                            ) : (
                                                dailyList.map((stock) => (
                                                    <motion.div
                                                        key={stock.symTicker || stock.symbol}
                                                        initial={{ opacity: 0, y: 5 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{ opacity: 0, scale: 0.95 }}
                                                        transition={{ duration: 0.12, ease: "easeOut" }}
                                                        className="group bg-[#22272b] border border-transparent hover:border-zinc-600 px-3 py-2 rounded-lg shadow-sm relative"
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex flex-col">
                                                                <span className="text-sm font-medium text-[#b6c2cf]">
                                                                    {stock.symTicker || stock.symticker || `NSE:${stock.symbol}`}
                                                                </span>
                                                                {stock.added_at && (
                                                                    <span className="text-[10px] text-zinc-500 mt-0.5">
                                                                        {formatDateTime(stock.added_at)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <button
                                                                onClick={() => setDeleteModal({ isOpen: true, stock, listType: 'daily' })}
                                                                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-white/10 text-zinc-400 hover:text-white rounded transition-all"
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </button>
                                                        </div>
                                                    </motion.div>
                                                ))
                                            )}
                                        </AnimatePresence>
                                    )}
                                </div>
                            </div>

                            {/* Weekly Column */}
                            <div className="w-[272px] flex flex-col max-h-full bg-[#246348] rounded-xl shadow-sm">
                                <div className="p-3 flex items-center justify-between">
                                    <h3 className="text-sm font-bold text-[#6ee7b7] flex items-center gap-1.5">
                                        Weekly
                                    </h3>
                                    <div className="flex items-center gap-2 text-[#6ee7b7] opacity-80">
                                        <ArrowRightLeft className="w-4 h-4 cursor-pointer hover:opacity-100" />
                                        <MoreHorizontal className="w-4 h-4 cursor-pointer hover:opacity-100" />
                                    </div>
                                </div>
                                <div className="flex-1 overflow-y-auto custom-scroll px-2 space-y-2 pb-2">
                                    {listsLoading ? (
                                        <div className="flex flex-col items-center justify-center py-10 gap-3">
                                            <div className="w-6 h-6 border-2 border-emerald-500/20 border-t-[#6ee7b7] rounded-full animate-spin"></div>
                                        </div>
                                    ) : (
                                        <AnimatePresence initial={false}>
                                            {weeklyList.length === 0 ? (
                                                <motion.div
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    transition={{ duration: 0.15 }}
                                                    className="flex flex-col items-center justify-center p-4 py-8 mt-2 text-center bg-[#6ee7b7]/5 rounded-lg border border-dashed border-[#6ee7b7]/20 mx-1"
                                                >
                                                    <Inbox className="w-8 h-8 text-[#6ee7b7]/40 mb-2" />
                                                    <p className="text-xs font-medium text-[#6ee7b7]/60">No Weekly {countryLabel} Stocks</p>
                                                    <p className="text-[10px] text-[#6ee7b7]/40 mt-1">Add from the inbox panel</p>
                                                </motion.div>
                                            ) : (
                                                weeklyList.map((stock) => (
                                                    <motion.div
                                                        key={stock.symTicker || stock.symbol}
                                                        initial={{ opacity: 0, y: 5 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{ opacity: 0, scale: 0.95 }}
                                                        transition={{ duration: 0.12, ease: "easeOut" }}
                                                        className="group bg-[#22272b] border border-transparent hover:border-zinc-600 px-3 py-2 rounded-lg shadow-sm relative"
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex flex-col">
                                                                <span className="text-sm font-medium text-[#b6c2cf]">
                                                                    {stock.symTicker || stock.symticker || `NSE:${stock.symbol}`}
                                                                </span>
                                                                {stock.added_at && (
                                                                    <span className="text-[10px] text-zinc-500 mt-0.5">
                                                                        {formatDateTime(stock.added_at)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <button
                                                                onClick={() => setDeleteModal({ isOpen: true, stock, listType: 'weekly' })}
                                                                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-white/10 text-zinc-400 hover:text-white rounded transition-all"
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </button>
                                                        </div>
                                                    </motion.div>
                                                ))
                                            )}
                                        </AnimatePresence>
                                    )}
                                </div>
                            </div>
                        </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <AnimatePresence>
                {deleteModal.isOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                            onClick={() => setDeleteModal({ isOpen: false, stock: null, listType: null })}
                        />
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 10 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 10 }}
                            className="relative bg-[#14233a] border border-[#1a2b4c] rounded-xl shadow-2xl p-6 w-full max-w-sm m-4 z-10"
                        >
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                                    <Trash2 className="w-5 h-5 text-red-500" />
                                </div>
                                <h3 className="text-lg font-bold text-white">Remove Stock</h3>
                            </div>
                            <p className="text-[#b6c2cf] text-sm mb-6">
                                Are you sure you want to remove <span className="font-bold text-white">{deleteModal.stock?.symTicker || deleteModal.stock?.symbol}</span> from the <span className="capitalize">{deleteModal.listType}</span> list?
                            </p>
                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => setDeleteModal({ isOpen: false, stock: null, listType: null })}
                                    className="px-4 py-2 text-sm font-medium text-zinc-300 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={executeDelete}
                                    disabled={deleteLoading}
                                    className="px-4 py-2 text-sm font-medium text-white bg-red-500/80 hover:bg-red-500 rounded-lg transition-colors shadow-lg shadow-red-500/20 disabled:opacity-70 flex items-center gap-2 min-w-[90px] justify-center"
                                >
                                    {deleteLoading ? (
                                        <>
                                            <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            <span>Removing...</span>
                                        </>
                                    ) : (
                                        "Remove"
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </DashboardLayout>
    );
};

export default ClearStocks;
